import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import special from "../styles/special.module.scss";
import { UserContext } from "../context/UserContext";
import useToken from "../utilities/hooks/useToken";
import { hasValidToken } from "../utilities/AuthState";
import registerAccount, {
  registerErrorMessage,
} from "../utilities/registerAccount";
import {
  formatBrandLabel,
  getAccountId,
  getStoredAccountEmail,
  isReservedGuestEmail,
  isSharedBrandSession,
} from "../utilities/loginAccount";

function RegisterAccount() {
  const navigate = useNavigate();
  const { userInformation, changeUserInformation } = useContext(UserContext);
  const { setToken } = useToken();
  const sharedBrandSession = isSharedBrandSession(userInformation);
  const lockedBrand = String(userInformation.vendor || "").trim().toLowerCase();
  const brandLabel = formatBrandLabel(lockedBrand) || "brand";

  const [userName, setUserName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [brand, setBrand] = useState(
    sharedBrandSession ? lockedBrand : userInformation.vendor || "",
  );
  const [userType, setUserType] = useState(
    sharedBrandSession ? "vendor" : "designer",
  );
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdEmail, setCreatedEmail] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [createdEmail]);

  useEffect(() => {
    if (!sharedBrandSession) {
      return;
    }

    setBrand(lockedBrand);
    setUserType("vendor");
  }, [lockedBrand, sharedBrandSession]);

  const registerVisualStyle = {
    backgroundImage: `linear-gradient(180deg, rgba(8, 12, 12, 0.12), rgba(8, 12, 12, 0.84)), url(${process.env.PUBLIC_URL}/images/contact.jpg)`,
  };

  const resetForm = () => {
    setUserName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setUserType(sharedBrandSession ? "vendor" : "designer");
    setBrand(sharedBrandSession ? lockedBrand : userInformation.vendor || "");
    setMessage("");
    setCreatedEmail("");
  };

  const signInAsCreatedUser = (response, loginIdentifier) => {
    const accessToken =
      response.accessToken || response.token || response.access_token;

    if (hasValidToken(accessToken)) {
      setToken(accessToken);
    }

    changeUserInformation({
      userId: getAccountId(response),
      userType: response.userType,
      vendor: response.brand,
      userName: response.userName,
      email: getStoredAccountEmail(response, loginIdentifier),
      isLoggedIn: true,
    });
    navigate("/library");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage("");

    const trimmedName = userName.trim();
    const trimmedEmail = email.trim().toLowerCase();
    const nextBrand = sharedBrandSession
      ? lockedBrand
      : String(brand || "").trim().toLowerCase();
    const nextUserType = sharedBrandSession ? "vendor" : userType;

    if (!trimmedName) {
      setMessage("Enter a name for this account.");
      return;
    }

    if (!trimmedEmail) {
      setMessage("Enter an email address.");
      return;
    }

    if (isReservedGuestEmail(trimmedEmail)) {
      setMessage("Choose a personal email for this account.");
      return;
    }

    if (!nextBrand) {
      setMessage("Brand is required.");
      return;
    }

    if (!password) {
      setMessage("Enter a password.");
      return;
    }

    if (password.length < 8) {
      setMessage("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setMessage("Passwords do not match.");
      return;
    }

    setLoading(true);

    try {
      const response = await registerAccount({
        userName: trimmedName,
        email: trimmedEmail,
        password,
        brand: nextBrand,
        userType: nextUserType,
      });

      if (sharedBrandSession) {
        signInAsCreatedUser(response, trimmedEmail);
        return;
      }

      setCreatedEmail(response?.userEmail || trimmedEmail);
    } catch (error) {
      setMessage(registerErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  if (createdEmail) {
    return (
      <div className={special.contactShell}>
        <div className={special.contactPage}>
          <div className={special.contactBackdrop}></div>
          <div className={special.contactFrame}>
            <div className={special.contactVisual} style={registerVisualStyle}>
              <div className={special.contactVisualOverlay}></div>
              <div className={special.contactVisualCopy}>
                <p className={special.contactVisualEyebrow}>Admin</p>
                <h2>Account created.</h2>
                <p>{createdEmail} can now sign in with the password you set.</p>
              </div>
            </div>

            <div className={special.contactForm}>
              <h1>Registration complete</h1>
              <p className={special.contactIntro}>
                You are still signed in as the admin. Share the email and
                password with this user, or register another account.
              </p>
              <div className={special.registerActions}>
                <button
                  type="button"
                  className={special.contactButton}
                  onClick={resetForm}
                >
                  Register another
                </button>
                <button
                  type="button"
                  className={special.registerSecondaryButton}
                  onClick={() => navigate("/library")}
                >
                  Back to library
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={special.contactShell}>
      <div className={special.contactPage}>
        <div className={special.contactBackdrop}></div>
        <div className={special.contactFrame}>
          <div className={special.contactVisual} style={registerVisualStyle}>
            <div className={special.contactVisualOverlay}></div>
            <div className={special.contactVisualCopy}>
              <p className={special.contactVisualEyebrow}>
                {sharedBrandSession ? brandLabel : "Admin"}
              </p>
              <h2>
                {sharedBrandSession
                  ? "Create your personal account."
                  : "Register a Matlib account."}
              </h2>
              <p>
                {sharedBrandSession
                  ? `This profile stays on ${brandLabel} as a Vendor account.`
                  : "Create designer or vendor logins. This is not a public signup form."}
              </p>
            </div>
          </div>

          <form className={special.contactForm} onSubmit={handleSubmit}>
            <h1>
              {sharedBrandSession ? "Create personal account" : "Register account"}
            </h1>
            <p className={special.contactIntro}>
              {sharedBrandSession
                ? `Choose a name, personal email, and password. Brand and account type stay locked to this ${brandLabel} Vendor profile.`
                : "New users receive the email and password you enter here. They can change that password after they sign in."}
            </p>

            <div className={special.contactInputGroup}>
              <label htmlFor="register-name">Name</label>
              <input
                id="register-name"
                type="text"
                autoComplete="name"
                placeholder="Account name"
                value={userName}
                onChange={(event) => setUserName(event.target.value)}
              />
            </div>

            <div className={special.contactInputGroup}>
              <label htmlFor="register-email">Email</label>
              <input
                id="register-email"
                type="email"
                autoComplete="off"
                placeholder="you@brand.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <div className={special.contactInputGroup}>
              <label htmlFor="register-brand">Brand</label>
              <input
                id="register-brand"
                type="text"
                autoComplete="organization"
                placeholder="Brand"
                value={sharedBrandSession ? brandLabel : brand}
                readOnly={sharedBrandSession}
                disabled={sharedBrandSession}
                onChange={(event) => setBrand(event.target.value)}
              />
            </div>

            <div className={special.contactInputGroup}>
              <label htmlFor="register-type">Account type</label>
              <select
                id="register-type"
                value={userType}
                disabled={sharedBrandSession}
                onChange={(event) => setUserType(event.target.value)}
              >
                {sharedBrandSession ? null : (
                  <option value="designer">Designer</option>
                )}
                <option value="vendor">Vendor</option>
              </select>
            </div>

            <div className={special.contactInputGroup}>
              <label htmlFor="register-password">Password</label>
              <input
                id="register-password"
                type="password"
                autoComplete="new-password"
                placeholder="At least 8 characters"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </div>

            <div className={special.contactInputGroup}>
              <label htmlFor="register-confirm-password">Confirm password</label>
              <input
                id="register-confirm-password"
                type="password"
                autoComplete="new-password"
                placeholder="Repeat password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </div>

            {message ? (
              <div className={special.registerErrorMessage}>{message}</div>
            ) : null}

            <div className={special.registerActions}>
              <button
                type="submit"
                className={special.contactButton}
                disabled={loading}
              >
                {loading
                  ? "Creating account..."
                  : sharedBrandSession
                    ? "Create personal account"
                    : "Create account"}
              </button>
              <button
                type="button"
                className={special.registerSecondaryButton}
                onClick={() => navigate("/library")}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default RegisterAccount;
